import { WEI, } from "@bracket/blocksmith";
import { getAggregateError } from "@bracket/catch";
import { contract, multiCaller } from "@bracket/chainography/contracts";
import * as CACHE from "@cachables";
import type { IBracketStore, } from "@types";
import { getNextEpochStartDate } from "@vault/resolvers/epoch";
import { wagmiConfig as config, } from "@wagmiconfig";
import { getTokenBalances } from "../getTokenBalances";
import type { IdentifiableVaultWithState, } from "../loaded/types";
import { getManagedVault } from "./getManagedVault";
import { getManagerBalances } from "./getManagerBalances";
import type { ManagedVault, ManagedVaultData, } from "./types";


/**
 * Retrieves and processes management data for a vault.
 * 
 * @param vaultI - The vault identifier to fetch management data for
 * @returns {Promise<ManagedVaultData>} The processed vault management data
 * 
 * @throws {Error} If the vault cannot be loaded or managed
 * 
 * @remarks
 * This function:
 * 1. Loads the vault into a manageable state if not already
 * 2. Returns initial data if the vault is not active
 * 3. Fetches base management data
 * 4. Applies version-specific extensions to the data
 * 5. Returns the complete management data object
 */
export const getManagedVaultData = async (
	store: IBracketStore,
	vaultI: IdentifiableVaultWithState,
): Promise<ManagedVaultData> => {

	try {
		const VAULT = ["MANAGEABLE", "LOADED"].includes(vaultI.state)
			? vaultI as ManagedVault
			: await getManagedVault(store, { ...vaultI, state: "MANAGEABLE" });

		if(VAULT.status !== "ACTIVE") {
			return initialData;
		}

		let result: ManagedVaultData = await getBaseData(store, VAULT);
		const ver = Number.parseInt(VAULT.version || "1", 10);
		for(let i = 0; i < ver; i++) {
			result = await extensionFunctions[i](result);
		}

		return result;
	}
	catch(err) {

		throw getAggregateError(err, `getManagedVaultData failed for vault ${vaultI.symbol}`);
	}

}


const initialData: ManagedVaultData = {
	sharesOutstanding: 0n,
	totalWithdrawals: 0n,
	totalWithdrawalsNext: 0n,
	totalDeposits: 0n,
	pendingDeposits: 0n,
	colateralBalances: [],
	managerBalances: [],
	vanityNav: 0n,
	strategyTVL: {
		usd: 0,
		brkt: "0",
	},
	nextEpochStartTime: "UNKNOWN",
	estAmountDue: 0n,
	vaultVariance: 0n,
	accruedManagerPerformanceFees: 0n,
	accruedManagerTvlFees: 0n,
	accruedBrktTvlFees: 0n,
	currentEpochClosable: false,
	navFees: 0n,
	accruedTvlFees: 0n
}


const getBaseData = async (
	store: IBracketStore,
	vault: ManagedVault,
): Promise<ManagedVaultData> => {

	try {

		const { denomination, nav : { manager_tvl_fee, bracket_tvl_fee, }, } = vault;
		const managerBalances = await getManagerBalances(vault);
		const managerDenoCurr = managerBalances.find(curr => curr.symbol === vault.denomination.symbol);
		if(!managerDenoCurr) {
			throw getAggregateError(new Error("Manager denomination balance not found"), `getBaseData failed for vault ${vault.symbol}`);
		}
		const managerDenoBalance = managerDenoCurr.value;
		const vaultContractName = vault.version === "2" ? "BracketVaultV2" : "BracketVault";

		const [ [
				totalDeposits,
				totalWithdrawalsShares,
				totalWithdrawalsNext,
				vanityNav,
				accruedManagerPerformanceFees,
				accruedManagerTvlFees,
				accruedBrktTvlFees,
		], errors, ] = await multiCaller<[bigint, bigint, bigint, bigint, bigint, bigint, bigint, bigint]>(config, [
			contract(vaultContractName, "totalDeposits", vault.address),
			contract(vaultContractName, "totalWithdrawals", vault.address, vault.epoch),
			contract(vaultContractName, "totalWithdrawals", vault.address, vault.epoch + 1),
			contract(vaultContractName, "vanityNav", vault.address),
			contract(vaultContractName, "accruedManagerPerformanceFees", vault.address),
			contract(vaultContractName, "accruedManagerTvlFees", vault.address),
			contract(vaultContractName, "accruedBrktTvlFees", vault.address),
		]);

		if(errors) {

			throw getAggregateError(errors, `getBaseData failed for vault ${vault.symbol}`);
		}

		let navFees: bigint;
		let vaultVariance: bigint;
		let estAmountDue: bigint;
		let pendingDeposits: bigint;
		let accruedTvlFees: bigint;
		let currentEpochClosable: boolean;
		let totalWithdrawals: bigint;

		try {

			const wNav = WEI.fromSync(vault.nav.nav, denomination);
			const wTotalDeposits = WEI.fromSync(totalDeposits, denomination);
			const wTotalWithdrawals = WEI.fromSync(totalWithdrawalsShares, denomination).multiply(wNav);
			const wManagerTvlFee = WEI.fromSync(manager_tvl_fee, denomination);
			const wBracketTvlFee = WEI.fromSync(bracket_tvl_fee, denomination);
			const wAccruedManagerTvlFees = WEI.fromSync(accruedManagerTvlFees, denomination);
			const wAccruedBrktTvlFees = WEI.fromSync(accruedBrktTvlFees, denomination);
			const wManagerDenoBalance = WEI.fromSync(managerDenoBalance, denomination);

			const wNavFees = wManagerTvlFee.add(wBracketTvlFee).multiply(wNav);
			const wEstAmountDue = wTotalDeposits.subtract(wTotalWithdrawals).subtract(wNavFees);
			const wVaultVariance = wEstAmountDue.add(wManagerDenoBalance);

			navFees = wNavFees.toBigInt();
			vaultVariance = wVaultVariance.toBigInt();
			estAmountDue = wEstAmountDue.toBigInt();
			pendingDeposits = wTotalDeposits.toBigInt(); // was ::: totalDeposits + 0n; --- w\tf?
			accruedTvlFees = wAccruedManagerTvlFees.add(wAccruedBrktTvlFees).toBigInt();
			totalWithdrawals = wTotalWithdrawals.toBigInt();
			currentEpochClosable = wVaultVariance.greaterThanOrEqual(0n);

		}
		catch(error) {

			throw getAggregateError(error, `getBaseData failed for vault ${vault.symbol}`);
		}

		const [
			strategyTVL,
			nextEpochStartTime,
			sharesOutstanding,
			colateralBalances,
		] = await Promise.all([
			CACHE.strategyTVL({ vault, }),
			getNextEpochStartDate(store, vault),
			CACHE.sharesOutstanding({ vault, }),
			getTokenBalances(),
		]);

		return {
			currentEpochClosable,
			vaultVariance,
			estAmountDue,
			vanityNav,
			strategyTVL,
			nextEpochStartTime,
			sharesOutstanding,
			pendingDeposits,
			totalWithdrawals,
			totalWithdrawalsNext,
			totalDeposits,
			colateralBalances,
			managerBalances,
			accruedManagerPerformanceFees,
			accruedManagerTvlFees,
			accruedBrktTvlFees,
			accruedTvlFees,
			navFees,
		};

	}
	catch(err) {

		throw getAggregateError(err, `getManagedVaultData::getBaseData failed for vault ${vault.symbol}`);
	}

}


const getManagedVaultDataV1 = async (
	data: ManagedVaultData,
): Promise<ManagedVaultData> => {
	return data;
}



const getManagedVaultDataV2 = async (
	data: ManagedVaultData,
): Promise<ManagedVaultData> => {
	return data;
}


const extensionFunctions = [
	getManagedVaultDataV1,
	getManagedVaultDataV2,
]
